// Overview page for Sunlight Grounds Care Client Portal
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import api from '@/api/client';
import { useProperty } from '@/hooks/useProperty';
import { ChevronRight } from 'lucide-react';

export default function Overview({ user }) {
  const { selectedProperty } = useProperty();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [propertyStaff, setPropertyStaff] = useState(null);
  const [services, setServices] = useState([]);
  const [recommendation, setRecommendation] = useState(null);
  const [nextRouteVisit, setNextRouteVisit] = useState(null);

  // Match route properties to client property by name
  const matchesProperty = useCallback((routeProperties, clientProperty) => {
    if (!clientProperty) return false;
    const clientName = (clientProperty.name || '').toLowerCase().trim();
    const clientAddr = (clientProperty.address || '').toLowerCase().trim();
    return routeProperties.some(rp => {
      const routeName = (rp.property_name || '').toLowerCase().trim();
      if (!routeName) return false;
      return clientName.includes(routeName) || clientAddr.includes(routeName) ||
             routeName.includes(clientName);
    });
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!selectedProperty) {
        setLoading(false);
        return;
      }
      
      try {
        const today = new Date().toISOString().split('T')[0];
        const results = await Promise.allSettled([
          api.getDashboardData(selectedProperty.id, today),
          api.getPropertyStaff(selectedProperty.id),
          api.getServices(selectedProperty.id),
          api.getRecommendations(selectedProperty.id),
          api.getGroundsCareRoutes()
        ]);
        
        if (results[0].status === 'fulfilled') setDashboardData(results[0].value);
        if (results[1].status === 'fulfilled') {
          const staff = results[1].value;
          const mapped = {};
          // Handle { assignments: [{ groundskeeper_name, property_manager_name }] }
          if (staff?.assignments && Array.isArray(staff.assignments) && staff.assignments.length > 0) {
            const a = staff.assignments[0];
            mapped.groundskeeper = a.groundskeeper_name || null;
            mapped.manager = a.property_manager_name || null;
          } else if (Array.isArray(staff)) {
            staff.forEach(s => {
              if (s.role === 'groundskeeper') mapped.groundskeeper = s.name;
              if (s.role === 'manager' || s.role === 'property_manager') mapped.manager = s.name;
            });
          }
          setPropertyStaff(mapped);
        }
        if (results[2].status === 'fulfilled') {
          // service-request-types: filter to only custom/property-specific types
          const svc = results[2].value;
          const all = Array.isArray(svc) ? svc : [];
          setServices(all.filter(s => !s.is_default));
        }
        if (results[3].status === 'fulfilled') {
          // recommendations: { recommendations: [...] } or direct array
          const rec = results[3].value;
          const recList = rec?.recommendations || (Array.isArray(rec) ? rec : []);
          setRecommendation(recList[0] || null);
        }
        // Match routes to selected property for next visit — only FUTURE dates
        if (results[4].status === 'fulfilled') {
          const allRoutes = results[4].value || [];
          const todayStr = new Date().toISOString().split('T')[0];
          const matched = allRoutes
            .filter(route => matchesProperty(route.properties || [], selectedProperty))
            .filter(route => route.date > todayStr)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
          setNextRouteVisit(matched[0] || null);
        }
      } catch (err) {
        console.error('Failed to load overview data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProperty, matchesProperty]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    // Append T12:00:00 to date-only strings to prevent UTC→local timezone shift
    const safe = dateStr.includes('T') ? dateStr : dateStr + 'T12:00:00';
    const date = new Date(safe);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'request':
        navigate('/requests', { state: { openNewRequest: true } });
        break;
      case 'message':
        window.location.href = 'sms:+16267522462';
        break;
      case 'walkthrough':
        navigate('/requests', { state: { openNewRequest: true, prefillType: 'Schedule a Walkthrough' } });
        break;
      case 'photos':
        navigate('/delivery');
        break;
      default:
        break;
    }
  };

  const handleGetQuote = async () => {
    if (!recommendation || !selectedProperty) return;
    
    try {
      await api.createServiceRequest({
        property_id: selectedProperty.id,
        request_type: recommendation.service,
        service_type: recommendation.service.toLowerCase().replace(' ', '_'),
        location_text: 'Full property',
        description: `Enhancement request: ${recommendation.title}`
      });
      
      navigate('/comms', { state: { newRequest: true } });
    } catch (err) {
      console.error('Failed to create enhancement request:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-light">
        <Header showGreeting userName={user?.name} property={selectedProperty} />
        <div className="p-4">
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="sg-card p-4 animate-pulse">
                <div className="h-3 bg-slate/20 rounded w-16 mb-3"></div>
                <div className="h-6 bg-slate/20 rounded w-12"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Map real dashboard data to display values — only show FUTURE dates for next visit
  const todayStr = new Date().toISOString().split('T')[0];
  const futureSchedule = (dashboardData?.schedule || []).find(s => s.date > todayStr);
  const nextVisit = futureSchedule?.date || nextRouteVisit?.date;
  const photoSummary = dashboardData?.photo_summary || {};
  const requestsSummary = dashboardData?.service_requests_summary || {};
  const openRequests = (requestsSummary.pending || 0) + (requestsSummary.in_progress || 0);

  return (
    <div className="min-h-screen bg-slate-light pb-24" data-testid="overview-page">
      <Header showGreeting userName={user?.name} property={selectedProperty} />
      
      <div className="p-4 sm:p-6 md:p-8 max-w-4xl mx-auto">
        {/* Stat Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="stat-tiles">
          {/* Next Visit */}
          <div className="sg-card p-4">
            <p className="sg-label mb-1">Next visit</p>
            <p className="font-cormorant text-2xl font-semibold text-navy">
              {nextVisit ? formatDate(nextVisit) : 'TBD'}
            </p>
            <span className={`inline-block mt-2 text-[9px] font-dm font-medium px-2 py-0.5 rounded-full ${
              nextVisit ? 'bg-green-light text-green' : 'bg-gold-light text-[#996B00]'
            }`}>
              {nextVisit ? 'Scheduled' : 'Not scheduled'}
            </span>
          </div>

          {/* Photos */}
          <div className="sg-card p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/delivery')}>
            <p className="sg-label mb-1">Photos</p>
            <p className="font-cormorant text-2xl font-semibold text-navy">
              {photoSummary.total_photos || 0}
            </p>
            <span className="inline-block mt-2 text-[9px] font-dm font-medium px-2 py-0.5 rounded-full bg-navy-light text-navy">
              {photoSummary.days_with_photos || 0} {(photoSummary.days_with_photos || 0) === 1 ? 'visit' : 'visits'}
            </span>
          </div>

          {/* Open Requests */}
          <div
            className="sg-card p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/requests')}
            data-testid="open-requests-card"
          >
            <p className="sg-label mb-1">Open requests</p>
            <p className="font-cormorant text-2xl font-semibold text-navy">
              {openRequests}
            </p>
            <span className={`inline-block mt-2 text-[9px] font-dm font-medium px-2 py-0.5 rounded-full ${
              openRequests > 0 
                ? 'bg-gold-light text-[#996B00]' 
                : 'bg-green-light text-green'
            }`}>
              {openRequests > 0 ? 'Pending' : 'None'}
            </span>
          </div>

          {/* Property Health */}
          <div className="sg-card p-4">
            <p className="sg-label mb-1">Property health</p>
            <p className="font-cormorant text-2xl font-semibold text-green">
              Excellent
            </p>
            <span className="inline-block mt-2 text-[9px] font-dm font-medium px-2 py-0.5 rounded-full bg-green-light text-green">
              Active
            </span>
          </div>
        </div>

        {/* Your Team Card */}
        <p className="sg-label pt-4 mb-2">Your team</p>
        <div className="sg-card mb-4" data-testid="team-card">
          <div className="sg-info-row">
            <span className="text-slate">Groundskeeper</span>
            <span className="font-medium text-navy">{propertyStaff?.groundskeeper || '—'}</span>
          </div>
          <div className="sg-info-row">
            <span className="text-slate">Property manager</span>
            <span className="font-medium text-navy">{propertyStaff?.manager || '—'}</span>
          </div>
          <div className="sg-info-row border-b-0">
            <span className="text-slate">Plan</span>
            <span className="font-medium text-navy">{propertyStaff?.plan || 'Standard'} · Active</span>
          </div>
        </div>

        {/* Quick Actions */}
        <p className="sg-label pt-2 mb-2">Quick actions</p>
        <div className="sg-card p-3" data-testid="quick-actions-card">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            <button 
              onClick={() => handleQuickAction('request')}
              className="bg-slate-light rounded-xl p-3 text-left hover:bg-slate-light/70 transition-colors"
              data-testid="action-request"
            >
              <span className="text-base">📋</span>
              <p className="font-dm text-[11px] font-bold text-navy mt-1.5">Request service</p>
              <p className="font-dm text-[9px] text-slate">Add or change</p>
            </button>
            
            <button 
              onClick={() => handleQuickAction('message')}
              className="bg-slate-light rounded-xl p-3 text-left hover:bg-slate-light/70 transition-colors"
              data-testid="action-message"
            >
              <span className="text-base">💬</span>
              <p className="font-dm text-[11px] font-bold text-navy mt-1.5">Message manager</p>
              <p className="font-dm text-[9px] text-slate">Direct line</p>
            </button>
            
            <button 
              onClick={() => handleQuickAction('walkthrough')}
              className="bg-slate-light rounded-xl p-3 text-left hover:bg-slate-light/70 transition-colors"
              data-testid="action-walkthrough"
            >
              <span className="text-base">📅</span>
              <p className="font-dm text-[11px] font-bold text-navy mt-1.5">Schedule walkthrough</p>
              <p className="font-dm text-[9px] text-slate">Book a visit</p>
            </button>
            
            <button 
              onClick={() => handleQuickAction('photos')}
              className="bg-slate-light rounded-xl p-3 text-left hover:bg-slate-light/70 transition-colors"
              data-testid="action-photos"
            >
              <span className="text-base">📸</span>
              <p className="font-dm text-[11px] font-bold text-navy mt-1.5">View photos</p>
              <p className="font-dm text-[9px] text-slate">Service delivery</p>
            </button>
          </div>
        </div>

        {/* Your Services — only show if there are active services */}
        {services.length > 0 && (
          <>
            <p className="sg-label pt-4 mb-2">Your services</p>
            <div className="sg-card p-3" data-testid="services-card">
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
                {services.map((service, idx) => (
                  <div 
                    key={idx}
                    className="flex-shrink-0 bg-green-light rounded-xl px-3 py-2.5 text-center min-w-[90px]"
                  >
                    <p className="font-dm text-[10px] font-bold text-green mt-1">{service.name}</p>
                    <p className="font-dm text-[9px] text-slate">{service.description || ''}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Recommended Enhancement */}
        {recommendation && (
          <>
            <p className="sg-label pt-4 mb-2">Recommended</p>
            <div className="sg-card overflow-hidden" data-testid="recommendation-card">
              <div className="p-3">
                <h3 className="font-dm text-xs font-bold text-navy mb-1">{recommendation.title || recommendation.name}</h3>
                <p className="font-dm text-[10px] text-slate leading-relaxed mb-3">
                  {recommendation.description}
                </p>
                {(recommendation.price_low || recommendation.price) && (
                  <button 
                    onClick={handleGetQuote}
                    className="w-full bg-navy text-white font-dm text-[11px] font-medium py-2.5 rounded-xl
                               hover:bg-navy-light transition-colors flex items-center justify-center gap-1"
                    data-testid="get-quote-btn"
                  >
                    {recommendation.price_low ? `Get quote · $${recommendation.price_low}–$${recommendation.price_high}` : 'Request info'}
                    <ChevronRight size={14} />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
